import { Checkbox } from "@/components/ui/checkbox"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useEffect, useState, type ReactNode } from "react"
import { MasterDataList } from "../../../master-data/MasterDataList"
import { MasterDataSearch } from "../../../master-data/MasterDataSearch"
import { EntityListRow } from "../../../shared/EntityListRow"

type EntityPickerListResult<T> = {
  data?: { pages: { items: T[] }[] }
  hasNextPage?: boolean
  fetchNextPage: () => void
  isFetchingNextPage: boolean
  isFetching: boolean
  isLoading: boolean
}

/** Shared shell behind {@link CustomerPickerTable}/{@link ProductPickerTable} —
 *  the Conformity → Generate multi-select pickers. Locally scoped to this
 *  `editor/` dir, not `shared/master-data`, so the two pickers can share code
 *  with each other without cross-coupling to the Master Data surface itself
 *  (per Ferdi's "don't create a new interaction pattern, base it on the
 *  Master DB data view" note — that note is about the Master Data view, not
 *  about these two pickers sharing an implementation). */
export function EntityPickerTable<T extends { id: string; name: string }>({
  selectedIds,
  onToggle,
  useList,
  emptyLabelKey,
  entityLabelKey,
  renderContent,
  hiddenIds,
}: {
  selectedIds: Set<string>
  onToggle: (id: string) => void
  /** Rows to leave out entirely — e.g. products already linked to the
   *  customer, where re-picking would overwrite stored link data. */
  hiddenIds?: Set<string>
  useList: (params: { q?: string; limit: number }) => EntityPickerListResult<T>
  emptyLabelKey: TranslationKeys
  entityLabelKey: TranslationKeys
  renderContent: (row: T) => ReactNode
}) {
  const { translate } = useTranslation()
  const [search, setSearch] = useState("")
  // Same { limit: 200 } shape as GenerateConformityPanel's unfiltered fetch
  // (both omit `q`) so the two hit the identical react-query cache key —
  // arriving here from that panel's bulk selection reuses its already-loaded
  // pages instead of re-fetching the whole list from scratch.
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useList({ q: search || undefined, limit: 200 })

  const allRows = data?.pages.flatMap((p) => p.items) ?? []
  const rows = hiddenIds ? allRows.filter((r) => !hiddenIds.has(r.id)) : allRows

  // Every selected id already has a matching row loaded — no need to keep
  // paging a large org's list just because more pages exist.
  const loadedIds = new Set(rows.map((r) => r.id))
  const allSelectedLoaded =
    selectedIds.size === 0 || [...selectedIds].every((id) => loadedIds.has(id))

  // Arriving with a preselection (from the master-data bulk multi-select) —
  // page only as far as needed to resolve every selected id, so a
  // preselected row beyond the first page still renders checked without
  // fetching the rest of the list once they're all found. `isFetchingNextPage`
  // is in the deps (not just `hasNextPage`) so the effect re-fires each time
  // a page settles — `hasNextPage` alone can stay `true` across an entire
  // multi-page load and never re-trigger this effect on its own.
  useEffect(() => {
    if (allSelectedLoaded || search || !hasNextPage || isFetchingNextPage)
      return
    fetchNextPage()
  }, [
    allSelectedLoaded,
    search,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ])

  if (rows.length === 0 && !isLoading && !search) {
    return (
      <p className="rounded-md border border-dashed border-grey4 bg-grey2/40 py-8 text-center text-[13px] text-grey5">
        {translate(emptyLabelKey)}
      </p>
    )
  }

  return (
    <div className="flex h-[420px] flex-col overflow-hidden rounded-md border border-grey4">
      <MasterDataSearch
        value={search}
        onChange={setSearch}
        loading={!!search && isFetching}
        placeholder={translate("MC_MD_SEARCH_PLACEHOLDER", {
          entity: translate(entityLabelKey),
        })}
      />
      <MasterDataList
        rows={rows}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onFetchMore={fetchNextPage}
        renderRow={(row) => (
          <EntityListRow
            key={row.id}
            rowId={row.id}
            isSelected={selectedIds.has(row.id)}
            onClick={() => onToggle(row.id)}
            checkbox={
              <Checkbox
                checked={selectedIds.has(row.id)}
                onCheckedChange={() => onToggle(row.id)}
                aria-label={translate("MC_CS_CONF_GEN_PICKER_ARIA_SELECT", {
                  name: row.name,
                })}
              />
            }
          >
            {renderContent(row)}
          </EntityListRow>
        )}
      />
    </div>
  )
}
